// Category → display color (CSS hex)
export const CATEGORY_COLORS = {
  station:      '#FFD700',
  starlink:     '#00E5FF',
  oneweb:       '#38BDF8',
  kuiper:       '#7DD3FC',
  weather:      '#69FF94',
  gps:          '#B0C4FF',
  science:      '#C084FC',
  amateur:      '#94A3B8',
  active:       '#64748B',
  meo:          '#818CF8',
  geo:          '#F472B6',
  debris:       '#FF990066',
  reentry:      '#FF4444',
  groundstation:'#60A5FA',
  aviation:     '#e2e8f0',
  conjunction:  '#FFD700',
  aurora:       '#69FF94',
  launches:     '#ffffff',
  default:      '#94A3B8',
};

// Canvas billboard generator, distinct shapes + glow halos per category
const _cache = new Map();

// ── Shape drawers ─────────────────────────────────────────────────────────────

function drawStation(ctx, cx, r, color) {
  // ISS / CSS, hub with 4 solar panel arms
  const arm = r * 0.85;
  const panelW = r * 0.55;
  const panelH = r * 0.22;
  ctx.strokeStyle = color; ctx.lineWidth = 1.5;
  // Horizontal beam
  ctx.beginPath(); ctx.moveTo(cx - arm, cx); ctx.lineTo(cx + arm, cx); ctx.stroke();
  // Vertical beam (shorter)
  ctx.beginPath(); ctx.moveTo(cx, cx - r * 0.35); ctx.lineTo(cx, cx + r * 0.35); ctx.stroke();
  // Solar panels, 4 rectangles
  [[-arm, -panelH], [-arm + panelW * 0.05, -panelH],
   [cx - arm, cx - panelH / 2], [cx + arm - panelW, cx - panelH / 2]].forEach(() => {});
  const panels = [
    [cx - arm - panelW * 0.05, cx - panelH / 2],
    [cx + arm - panelW * 0.95, cx - panelH / 2],
  ];
  panels.forEach(([px, py]) => {
    ctx.fillStyle = color + '99';
    ctx.fillRect(px, py, panelW, panelH);
    ctx.strokeStyle = color; ctx.lineWidth = 0.8;
    ctx.strokeRect(px, py, panelW, panelH);
    // Panel grid lines
    ctx.beginPath();
    ctx.moveTo(px + panelW / 3, py); ctx.lineTo(px + panelW / 3, py + panelH);
    ctx.moveTo(px + panelW * 2 / 3, py); ctx.lineTo(px + panelW * 2 / 3, py + panelH);
    ctx.stroke();
  });
  // Central hub
  ctx.beginPath(); ctx.arc(cx, cx, r * 0.28, 0, Math.PI * 2);
  ctx.fillStyle = color; ctx.fill();
}

function drawConstellation(ctx, cx, r, color) {
  // Starlink/OneWeb/Kuiper, flat elongated diamond (visor sat)
  ctx.fillStyle = color + 'cc';
  ctx.strokeStyle = color; ctx.lineWidth = 1;
  // Long horizontal body
  ctx.beginPath();
  ctx.moveTo(cx - r, cx);
  ctx.lineTo(cx - r * 0.3, cx - r * 0.35);
  ctx.lineTo(cx + r, cx);
  ctx.lineTo(cx - r * 0.3, cx + r * 0.35);
  ctx.closePath(); ctx.fill(); ctx.stroke();
  // Solar panel lines
  ctx.strokeStyle = color + '88'; ctx.lineWidth = 0.7;
  ctx.beginPath();
  ctx.moveTo(cx - r * 0.8, cx); ctx.lineTo(cx - r * 0.2, cx);
  ctx.moveTo(cx + r * 0.1, cx); ctx.lineTo(cx + r * 0.85, cx);
  ctx.stroke();
}

function drawGEO(ctx, cx, r, color) {
  // GEO, square (stable, geostationary feel)
  const s = r * 0.75;
  ctx.fillStyle = color + 'aa';
  ctx.strokeStyle = color; ctx.lineWidth = 1.2;
  ctx.fillRect(cx - s, cx - s, s * 2, s * 2);
  ctx.strokeRect(cx - s, cx - s, s * 2, s * 2);
  // Corner marks
  const m = 2;
  [[cx - s, cx - s], [cx + s, cx - s], [cx - s, cx + s], [cx + s, cx + s]].forEach(([x, y]) => {
    ctx.beginPath(); ctx.arc(x, y, m, 0, Math.PI * 2);
    ctx.fillStyle = color; ctx.fill();
  });
}

function drawNavigation(ctx, cx, r, color) {
  // GPS/MEO, diamond with 4 cardinal points (nav crosshair)
  ctx.fillStyle = color + 'bb';
  ctx.strokeStyle = color; ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx, cx - r);
  ctx.lineTo(cx + r * 0.6, cx);
  ctx.lineTo(cx, cx + r);
  ctx.lineTo(cx - r * 0.6, cx);
  ctx.closePath(); ctx.fill(); ctx.stroke();
  // Center dot
  ctx.beginPath(); ctx.arc(cx, cx, r * 0.2, 0, Math.PI * 2);
  ctx.fillStyle = '#000'; ctx.fill();
}

function drawWeather(ctx, cx, r, color) {
  // Weather sat, circle with orbital ring
  ctx.beginPath(); ctx.arc(cx, cx, r * 0.45, 0, Math.PI * 2);
  ctx.fillStyle = color; ctx.fill();
  // Orbital ring (ellipse)
  ctx.strokeStyle = color + 'aa'; ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.ellipse(cx, cx, r * 0.9, r * 0.4, Math.PI / 4, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(cx, cx, r * 0.4, r * 0.9, Math.PI / 4, 0, Math.PI * 2);
  ctx.stroke();
}

function drawScience(ctx, cx, r, color) {
  // Science/Amateur, hexagon
  ctx.fillStyle = color + 'aa';
  ctx.strokeStyle = color; ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i - Math.PI / 6;
    const x = cx + r * 0.85 * Math.cos(a);
    const y = cx + r * 0.85 * Math.sin(a);
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  ctx.closePath(); ctx.fill(); ctx.stroke();
  // Center dot
  ctx.beginPath(); ctx.arc(cx, cx, r * 0.22, 0, Math.PI * 2);
  ctx.fillStyle = color; ctx.fill();
}

function drawDebris(ctx, cx, r, color) {
  // Debris, irregular jagged fragment
  ctx.fillStyle = color + '88';
  ctx.strokeStyle = color + 'aa'; ctx.lineWidth = 0.8;
  const pts = [
    [0.3, -0.9], [0.8, -0.4], [0.95, 0.1],
    [0.5, 0.7],  [0, 0.55],   [-0.6, 0.8],
    [-0.9, 0.2], [-0.7, -0.5],[-0.1, -0.85],
  ];
  ctx.beginPath();
  pts.forEach(([dx, dy], i) => {
    const x = cx + dx * r * 0.85;
    const y = cx + dy * r * 0.85;
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  });
  ctx.closePath(); ctx.fill(); ctx.stroke();
}

function drawActive(ctx, cx, r, color) {
  // Generic active payload, simple circle with tick marks
  ctx.beginPath(); ctx.arc(cx, cx, r * 0.65, 0, Math.PI * 2);
  ctx.fillStyle = color + 'bb'; ctx.fill();
  ctx.strokeStyle = color; ctx.lineWidth = 1;
  // 4 tick marks at cardinal points
  for (let i = 0; i < 4; i++) {
    const a = (Math.PI / 2) * i;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * r * 0.7, cx + Math.sin(a) * r * 0.7);
    ctx.lineTo(cx + Math.cos(a) * r * 0.95, cx + Math.sin(a) * r * 0.95);
    ctx.stroke();
  }
}

// ── Glow halo ─────────────────────────────────────────────────────────────────

function drawGlow(ctx, cx, size, color, alpha = 0.35) {
  const grad = ctx.createRadialGradient(cx, cx, size * 0.08, cx, cx, cx);
  grad.addColorStop(0,   color + Math.round(alpha * 255).toString(16).padStart(2,'0'));
  grad.addColorStop(0.45, color + Math.round(alpha * 0.5 * 255).toString(16).padStart(2,'0'));
  grad.addColorStop(1,   color + '00');
  ctx.beginPath(); ctx.arc(cx, cx, cx, 0, Math.PI * 2);
  ctx.fillStyle = grad; ctx.fill();
}

// ── Selection ring ────────────────────────────────────────────────────────────

function drawSelectionRing(ctx, cx, r, color) {
  // Dashed outer ring
  ctx.setLineDash([3, 2]);
  ctx.beginPath(); ctx.arc(cx, cx, r * 1.15, 0, Math.PI * 2);
  ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1; ctx.stroke();
  ctx.setLineDash([]);
  // Corner brackets at 4 quadrants
  const br = r * 1.25; const bl = r * 0.3;
  [0, 90, 180, 270].forEach(deg => {
    const rad = (deg * Math.PI) / 180;
    const bx = cx + br * Math.cos(rad);
    const by = cx + br * Math.sin(rad);
    ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(bx + Math.cos(rad + Math.PI/2) * bl, by + Math.sin(rad + Math.PI/2) * bl);
    ctx.lineTo(bx, by);
    ctx.lineTo(bx + Math.cos(rad - Math.PI/2) * bl, by + Math.sin(rad - Math.PI/2) * bl);
    ctx.stroke();
  });
}

// ── Tracked ring (larger, brighter) ───────────────────────────────────────────

function drawTrackedRing(ctx, cx, r, color) {
  // Solid bright outer ring
  ctx.beginPath(); ctx.arc(cx, cx, r * 1.1, 0, Math.PI * 2);
  ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.stroke();
  // Second ring
  ctx.beginPath(); ctx.arc(cx, cx, r * 1.28, 0, Math.PI * 2);
  ctx.strokeStyle = color + '66'; ctx.lineWidth = 1; ctx.stroke();
}

// ── Main builder ──────────────────────────────────────────────────────────────

export function buildBillboard(category, isSelected, isTracked) {
  const key = `${category}_${isSelected}_${isTracked}`;
  if (_cache.has(key)) return _cache.get(key);

  // Canvas is larger to accommodate glow + rings
  const baseR  = isTracked ? 10 : isSelected ? 8 : 6;
  const margin = isTracked ? 14 : isSelected ? 12 : 10;
  const size   = (baseR + margin) * 2;
  const cx     = size / 2;
  const r      = baseR;

  const canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext('2d');

  const rawColor = (CATEGORY_COLORS[category] || CATEGORY_COLORS.default);
  const color = rawColor.length > 7 ? rawColor.substring(0, 7) : rawColor;

  // 1, glow halo (always, stronger for selected/tracked)
  const glowAlpha = isTracked ? 0.55 : isSelected ? 0.45 : 0.25;
  drawGlow(ctx, cx, size, color, glowAlpha);

  // 2, shape
  ctx.save(); ctx.translate(0, 0);
  switch (category) {
    case 'station':                  drawStation(ctx, cx, r, color);       break;
    case 'starlink':
    case 'oneweb':
    case 'kuiper':                   drawConstellation(ctx, cx, r, color); break;
    case 'geo':                      drawGEO(ctx, cx, r, color);           break;
    case 'gps':
    case 'meo':                      drawNavigation(ctx, cx, r, color);    break;
    case 'weather':                  drawWeather(ctx, cx, r, color);       break;
    case 'science':
    case 'amateur':                  drawScience(ctx, cx, r, color);       break;
    case 'debris':                   drawDebris(ctx, cx, r, color);        break;
    default:                         drawActive(ctx, cx, r, color);        break;
  }
  ctx.restore();

  // 3, selection / tracked indicators on top
  if (isTracked)  drawTrackedRing(ctx, cx, r, color);
  if (isSelected) drawSelectionRing(ctx, cx, r, color);

  const url = canvas.toDataURL();
  _cache.set(key, url);
  return url;
}

// Reentry alert marker, pulsing amber warning
export function buildReentryMarker(daysLeft) {
  const key = `reentry-${Math.floor(daysLeft)}`;
  if (_cache.has(key)) return _cache.get(key);
  const size = 20;
  const canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext('2d');
  const cx = size / 2;
  // Urgency: 0-7 days = red, 7-14 = orange, 14-30 = amber
  const color = daysLeft < 7 ? '#ff4444' : daysLeft < 14 ? '#ff8800' : '#ffaa00';
  // Outer ring
  ctx.beginPath(); ctx.arc(cx, cx, cx - 1, 0, Math.PI * 2);
  ctx.strokeStyle = color; ctx.lineWidth = 1.5; ctx.stroke();
  // Warning triangle
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(cx, 4); ctx.lineTo(cx + 6, 15); ctx.lineTo(cx - 6, 15);
  ctx.closePath(); ctx.fill();
  // Exclamation dot
  ctx.fillStyle = '#000';
  ctx.beginPath(); ctx.arc(cx, 13, 1, 0, Math.PI * 2); ctx.fill();
  ctx.fillRect(cx - 1, 6, 2, 5);
  const url = canvas.toDataURL();
  _cache.set(key, url);
  return url;
}

// Ground station marker, dish icon
export function buildGroundStationMarker(type) {
  const key = `groundstation-${type}`;
  if (_cache.has(key)) return _cache.get(key);
  const size = 18;
  const canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext('2d');
  const cx = size / 2;
  const colors = { dsn: '#60a5fa', esoc: '#34d399', agency: '#a78bfa', satnogs: '#94a3b8' };
  const color = colors[type] || '#94a3b8';
  // Dish arc
  ctx.strokeStyle = color; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.arc(cx, cx + 2, 7, Math.PI, Math.PI * 2); ctx.stroke();
  // Dish stem
  ctx.beginPath(); ctx.moveTo(cx, cx + 2); ctx.lineTo(cx, size - 2);
  ctx.stroke();
  // Feed point
  ctx.fillStyle = color;
  ctx.beginPath(); ctx.arc(cx, 4, 2, 0, Math.PI * 2); ctx.fill();
  // Feed arm
  ctx.beginPath(); ctx.moveTo(cx, cx + 2); ctx.lineTo(cx, 4); ctx.stroke();
  const url = canvas.toDataURL();
  _cache.set(key, url);
  return url;
}
export function buildObserverMarker() {
  const key = 'observer-marker-bullseye';
  if (_cache.has(key)) return _cache.get(key);

  // Small, high-contrast target without a solid map pin or text on the globe.
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  ctx.scale(2, 2);
  const cx = 16;

  const glow = ctx.createRadialGradient(cx, cx, 4, cx, cx, 16);
  glow.addColorStop(0, 'rgba(94, 189, 255, 0.32)');
  glow.addColorStop(1, 'rgba(94, 189, 255, 0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(cx, cx, 16, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = 'rgba(4, 21, 40, 0.72)';
  ctx.beginPath();
  ctx.arc(cx, cx, 10, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = '#f5fbff';
  ctx.lineWidth = 2.3;
  ctx.beginPath();
  ctx.arc(cx, cx, 9, 0, Math.PI * 2);
  ctx.stroke();

  ctx.strokeStyle = '#69c5ff';
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.arc(cx, cx, 4.6, 0, Math.PI * 2);
  ctx.stroke();

  ctx.fillStyle = '#ffd166';
  ctx.beginPath();
  ctx.arc(cx, cx, 2, 0, Math.PI * 2);
  ctx.fill();

  const url = canvas.toDataURL();
  _cache.set(key, url);
  return url;
}

// Launch site marker, pulsing rocket icon
export function buildLaunchMarker(status) {
  const key = `launch-${status}`;
  if (_cache.has(key)) return _cache.get(key);
  const size = 20;
  const canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext('2d');
  const cx = size / 2;
  const color = status === 'Go' ? '#69FF94' : status === 'Hold' ? '#f59e0b' : '#e2e8f0';
  // Outer glow ring for Go status
  if (status === 'Go') {
    ctx.beginPath(); ctx.arc(cx, cx, cx - 1, 0, Math.PI * 2);
    ctx.strokeStyle = color + '88'; ctx.lineWidth = 2; ctx.stroke();
  }
  // Inner dot
  ctx.beginPath(); ctx.arc(cx, cx, 4, 0, Math.PI * 2);
  ctx.fillStyle = color; ctx.fill();
  // Simple upward arrow (launch indicator)
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(cx, 3); ctx.lineTo(cx + 3, 9); ctx.lineTo(cx - 3, 9);
  ctx.closePath(); ctx.fill();
  const url = canvas.toDataURL();
  _cache.set(key, url);
  return url;
}
export function buildAircraftMarker(heading) {
  const h = Math.round((heading || 0) / 10) * 10; // quantize to reduce cache entries
  const key = `aircraft-${h}`;
  if (_cache.has(key)) return _cache.get(key);
  const size = 10;
  const canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext('2d');
  const cx = size / 2;
  ctx.translate(cx, cx);
  ctx.rotate((h * Math.PI) / 180);
  ctx.fillStyle = '#cbd5e1';
  ctx.globalAlpha = 0.8;
  ctx.beginPath();
  ctx.moveTo(0, -(cx - 1));
  ctx.lineTo(cx - 1, cx - 1);
  ctx.lineTo(0, cx - 3);
  ctx.lineTo(-(cx - 1), cx - 1);
  ctx.closePath();
  ctx.fill();
  const url = canvas.toDataURL();
  _cache.set(key, url);
  return url;
}


// ── Target reticle, dominant ring + corner brackets for selected/tracked objects.
// This sits OVER the regular billboard so the user can always find the selected
// satellite at a glance, even in a dense swarm. Red color matches the orbital
// track palette and conflicts with no satellite category.
export function buildTargetReticle(state = 'selected') {
  const key = `reticle-${state}`;
  if (_cache.has(key)) return _cache.get(key);

  const size = 96;
  const canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext('2d');
  const cx = size / 2;

  const color = state === 'tracked' ? '#ff3030' : '#ff5050';
  const ringR = 28;
  const innerR = 16;

  // Soft outer halo
  const glow = ctx.createRadialGradient(cx, cx, ringR - 4, cx, cx, ringR + 16);
  glow.addColorStop(0, color + '00');
  glow.addColorStop(0.5, color + '33');
  glow.addColorStop(1, color + '00');
  ctx.beginPath(); ctx.arc(cx, cx, ringR + 16, 0, Math.PI * 2);
  ctx.fillStyle = glow; ctx.fill();

  // Main ring
  ctx.strokeStyle = color;
  ctx.lineWidth = state === 'tracked' ? 2.5 : 2;
  ctx.beginPath(); ctx.arc(cx, cx, ringR, 0, Math.PI * 2); ctx.stroke();

  // Inner dashed ring for tracked state
  if (state === 'tracked') {
    ctx.setLineDash([4, 3]);
    ctx.lineWidth = 1.2;
    ctx.beginPath(); ctx.arc(cx, cx, innerR, 0, Math.PI * 2); ctx.stroke();
    ctx.setLineDash([]);
  }

  // 4 corner brackets, ops-center targeting reticle
  const brLen = 10;
  const brOff = ringR + 6;
  ctx.lineWidth = 2;
  ctx.strokeStyle = color;
  [0, 90, 180, 270].forEach(deg => {
    const rad = (deg * Math.PI) / 180;
    const bx = cx + brOff * Math.cos(rad);
    const by = cx + brOff * Math.sin(rad);
    const tx = Math.cos(rad + Math.PI / 2);
    const ty = Math.sin(rad + Math.PI / 2);
    ctx.beginPath();
    ctx.moveTo(bx + tx * brLen, by + ty * brLen);
    ctx.lineTo(bx, by);
    ctx.lineTo(bx - tx * brLen, by - ty * brLen);
    ctx.stroke();
  });

  // Center crosshair tick marks (radial, not crossing through center)
  ctx.lineWidth = 1;
  [0, 90, 180, 270].forEach(deg => {
    const rad = (deg * Math.PI) / 180;
    const x1 = cx + (ringR - 6) * Math.cos(rad);
    const y1 = cx + (ringR - 6) * Math.sin(rad);
    const x2 = cx + (ringR - 1) * Math.cos(rad);
    const y2 = cx + (ringR - 1) * Math.sin(rad);
    ctx.beginPath();
    ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
    ctx.stroke();
  });

  const url = canvas.toDataURL();
  _cache.set(key, url);
  return url;
}
